import rawCardCollections from '../data/cards/CardCollections.json';
import rawCards from '../data/cards/Cards.json';
import { CardCollections, CardId } from '../data/cards/CardCollections.schema';
import { CardRarity, Cards } from '../data/cards/Cards.schema';
export type { CardId, CardRarity };
import { Match2Reward } from '../match2/rewards/match2-rewards.ruleset';
import { duration } from '../utils/duration';

/** Active card collection set matching the root asset folder. */
export const cardsCollectionSetId =
  '01_burumoments' as const satisfies keyof CardCollections;

/** Card collection id from the active cards collection data file. */
export type CollectionId = keyof CardCollections[typeof cardsCollectionSetId];

/** Semantic rarity tier used by Cards v2 drop and invite rules. */
export type CardTier = 'common' | 'rare' | 'legendary';

/** Ordered list of card tiers used when resolving weighted card drops. */
export const cardTiers: CardTier[] = ['common', 'rare', 'legendary'];

/** Card tiers that can be minted through card request invite links. */
export const cardsInviteMintableTiers: CardTier[] = ['common', 'rare'];

/**
 * Card request invites grant enough copies for the invitee to send one card to
 * the requester and keep one card in their own album.
 */
export const cardsInviteRequestGrantedCardCount = 2;

/** Map the existing 1-5 star rarity data to Cards v2 semantic tiers. */
export const cardTierByRarity: Record<CardRarity, CardTier> = {
  1: 'common',
  2: 'common',
  3: 'rare',
  4: 'rare',
  5: 'legendary',
};

/** Match2 level required to unlock the cards collection feature. */
export const cardsUnlockLevel = 30;

/** Raw card collection sets keyed by root asset folder id. */
export const cardCollectionSets = rawCardCollections as CardCollections;

/** Active card collections keyed by collection asset folder id. */
export const cardCollections = cardCollectionSets[cardsCollectionSetId];

/** Raw card metadata keyed by card id. */
export const cards = rawCards as Cards;

/** Derived card catalog metadata used by card selection and validation. */
export type Card = {
  /** Original 1-5 star rarity from the card data file. */
  rarity: CardRarity;
  /** Semantic Cards v2 tier derived from rarity. */
  tier: CardTier;
  /** Collection containing the card. */
  collection: CollectionId;
};

/** Derived catalog of available cards keyed by card id. */
export const cardsCatalog = Object.entries(cardCollections).reduce(
  (acc, [collection, { cards: collectionCards }]) => {
    for (const cardId of collectionCards) {
      const rarity = cards[cardId].rarity;
      acc[cardId as CardId] = {
        rarity,
        tier: cardTierByRarity[rarity],
        collection: collection as CollectionId,
      };
    }
    return acc;
  },
  {} as Record<CardId, Card>,
);

/** Inventory counts keyed by card id. */
export type CardsInventory = Partial<Record<CardId, number>>;

/** Reason a Match2 win can grant a Cards v2 level-win drop. */
export type CardsLevelWinDropSource =
  | 'firstDrop'
  | 'firstAttempt'
  | 'gemContinue';

/** Level-win card drop configuration. */
export type CardsLevelWinDropConfig = {
  /** Percent chance, from 0 to 100, for each eligible drop source. */
  dropChancePercentBySource: Record<CardsLevelWinDropSource, number>;
  /** Tier weights selected by minimum win streak reached. */
  streakTierWeights: {
    /** Minimum win streak required to use these tier weights. */
    minWinStreak: number;
    /** Relative weights for choosing a card tier. */
    weights: Record<CardTier, number>;
  }[];
};

/**
 * Card drop chances and tier weights for eligible match2 level wins.
 * The highest streak bracket whose minimum is reached supplies the tier weights.
 */
export const cardsLevelWinDropConfig: CardsLevelWinDropConfig = {
  dropChancePercentBySource: {
    firstDrop: 100,
    firstAttempt: 20,
    gemContinue: 33,
  },
  streakTierWeights: [
    {
      minWinStreak: 1,
      weights: { common: 70, rare: 25, legendary: 5 },
    },
    {
      minWinStreak: 6,
      weights: { common: 60, rare: 30, legendary: 10 },
    },
    {
      minWinStreak: 10,
      weights: { common: 30, rare: 50, legendary: 20 },
    },
  ],
};

/** UI color for each card collection. */
export const collectionColors: Record<CollectionId, string> = {
  '01_beach': '#7BDCD2',
  '02_gym': '#5D94FA',
  '03_shopping': '#FA5DA1',
  '04_pool': '#F0CE67',
  '05_cafe': '#E38A74',
  '06_onsen': '#6F7DE8',
  '07_camping': '#70C86B',
  '08_playground': '#ED6A6E',
  '09_disco': '#9B58FF',
};

/**
 * Grand prize rewards for fully completing the cards album.
 */
export const cardsAlbumCompleteRewards: Match2Reward[] = [
  { type: 'wrapper', id: 'all_powerups', amount: 10 },
  { type: 'gems', amount: 500 },
  { type: 'bPoints', amount: 1000 },
];

/**
 * Id for claiming rewards from a single collection or the full album.
 */
export type CardsRewardId = CollectionId | 'album';

/**
 * Claim status for a card collection or album reward.
 */
export type CardsRewardStatus = 'claimed' | 'available' | 'unavailable';

/** Ordered collection list used when iterating the cards album. */
export const cardsCollectionIds: CollectionId[] = [
  '01_beach',
  '02_gym',
  '03_shopping',
  '04_pool',
  '05_cafe',
  '06_onsen',
  '07_camping',
  '08_playground',
  '09_disco',
];

/** JST calendar-day limits for card sharing and trade initiation. */
export const tradeLimits = {
  /** Limit for cards sent by a user. */
  send: {
    /** Number of JST calendar days included in the send limit window. */
    durationDays: 1,
    /** Maximum cards the user can send during the send limit window. */
    amount: 5,
  },
  /** Limit for cards received by a user. */
  receive: {
    /** Number of JST calendar days included in the receive limit window. */
    durationDays: 1,
    /** Maximum cards the user can receive during the receive limit window. */
    amount: 5,
  },
  /** Limit for trades initiated by a user. */
  trade: {
    /** Number of JST calendar days included in the trade limit window. */
    durationDays: 1,
    /** Maximum trades the user can initiate during the trade limit window. */
    amount: 5,
  },
};

/** Duration each participant has to respond before a card trade expires. */
export const cardTradeExpirationMs = duration({ days: 1 });

/** Maximum number of actionable pending trades a user can have open. */
export const cardTradeMaxOpenTrades = 20;

/** Maximum number of trade candidates to display. */
export const cardsTradeCandidateLimit = 5;

/** Maximum number of card-request candidates to display. */
export const cardsRequestCandidateLimit = 20;
